import React, { useEffect, useState } from "react";
import { emitToast } from "./Toast";
import DatePickerCalendar from "./DatePickerCalendar";
import { HISTORY_MONTHS_BACK, monthsAgoYm } from "./dateUtil";
import ErrorBar from "./ErrorBar";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 休み申請フォーム本体（社員本人・セッション認証）。Porcelain テーマ。
// 申請ハブ(ApplyHubPage)のタブに載る部品として LeaveRequestBody を named export する。
// 申請履歴は種別横断の共通一覧(ApplyHistory)へ集約したため、ここは「新規申請フォーム」だけを持つ。
// 送信に成功したら onChanged() を呼び、親（ApplyHubPage）に共通の申請履歴を再取得させる。
// 経営者の承認は別ルート(/employee/leave-approval → LeaveApprovalPage)。旧 /employee/leave はハブへリダイレクト。
const API_URL = process.env.REACT_APP_API_URL || "";

// 休み申請は「単日」（1日）。既存BEは date_from/date_to を持つため、送信時に from=to=date にする。
const EMPTY_FORM = { type: "paid", date: "", reason: "" };

export function LeaveRequestBody({ onChanged }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // 選べない日（シフト休＋祝日）。過去日も申請できる（当日欠勤などの事後申請に対応）。
  // ※本番は BE がシフトパターン＋祝日(jpholiday)で返す。今はデモが返す。
  const [disabledSet, setDisabledSet] = useState(() => new Set());
  const [holidayNames, setHolidayNames] = useState(() => ({})); // { 日付: 祝日名 }
  // カレンダーで戻れる下限は過去12ヶ月（残業申請と統一）。過去日ロックは撤廃。
  const minYm = monthsAgoYm(HISTORY_MONTHS_BACK);
  const isDisabled = (ds) => disabledSet.has(ds);
  const isHoliday = (ds) => Object.prototype.hasOwnProperty.call(holidayNames, ds);
  const holidayName = (ds) => holidayNames[ds] || null;

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/employee/leave/unavailable/`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        // holidays は { 日付: 祝日名 }。配列で来た場合（名称なし）も一応許容する。
        const hol = Array.isArray(d.holidays)
          ? d.holidays.reduce((o, ds) => { o[ds] = ""; return o; }, {})
          : (d.holidays || {});
        setHolidayNames(hol);
        setDisabledSet(new Set([...(d.off || []), ...Object.keys(hol)]));
      })
      .catch(() => { /* 取得失敗時は日付制限なし（全日選択可）にフォールバック */ });
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    if (!form.date) { setError("日付を選択してください。"); return; }
    if (isDisabled(form.date)) { setError("その日は選べません（シフト休・祝日）。別の日を選んでください。"); return; }
    setSubmitting(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/leave/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        // 単日申請だが既存BEは期間(date_from/date_to)なので from=to=date で送る。
        body: JSON.stringify({ type: form.type, date_from: form.date, date_to: form.date, reason: form.reason }),
      });
      // BEの理由をそのまま出す（例:「シフト休・祝日には休み申請できません」）。
      // 固定文言に潰すと直し方が分からず同じ操作を繰り返すことになる。
      if (!res.ok) { setError(await apiErrorMessage(res, "申請の送信に失敗しました。")); return; }
      setForm(EMPTY_FORM);
      emitToast("申請しました。承認をお待ちください。");
      if (onChanged) onChanged();
    } catch (e) {
      setError("申請の送信に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      <h2 className="mp-h2">新規申請</h2>
      <div className="mp-panel" style={{ marginBottom: 28 }}>
        {/* 種別と日付は横並び（5:5・スマホでは縦積み）。 */}
        <div className="mp-formrow">
          <div className="mp-field">
            <label className="mp-flabel">種別</label>
            <select className="mp-select" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
              <option value="paid">有給</option>
              <option value="absence">欠勤</option>
              <option value="special">特別</option>
            </select>
          </div>
          <div className="mp-field">
            <label className="mp-flabel">日付</label>
            <DatePickerCalendar
              value={form.date}
              onChange={(ds) => setForm((p) => ({ ...p, date: ds }))}
              isDisabled={isDisabled}
              isHoliday={isHoliday}
              holidayName={holidayName}
              minYm={minYm}
              initialYm={monthsAgoYm(0)}
              placeholder="日付を選ぶ"
            />
          </div>
        </div>
        <div className="mp-field">
          <label className="mp-flabel">理由</label>
          <textarea className="mp-textarea" rows={2} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />
        </div>
        <button className="mp-btn mp-primary" disabled={submitting || !form.date} onClick={submit}>{submitting ? "送信中…" : "申請する"}</button>
      </div>
    </>
  );
}
